import React, { useCallback, useMemo, useState } from 'react';
import { Stack, Box } from '@mui/material';
import { Company, CompanyApplication, CompanyCertification, Contact } from '../../../interfaces/users.interface';
import StandardDialog from '../../Modals/StandardDialog';
import CompanyCertificationForm from './CompanyCertificationForm';
import { DataCell, DataRow } from '../../Tables/DataList';
import { tightTableSx } from '../../Tables/dataTableStyles';
import { CompanyCertificationDataRow } from './CompanyCertificationsAccordion';
import { getCompanyApps, getCompanyCertifications, getCompanySites } from '../../../services/companyDetail';
import { useCompanyResource } from '../../Vendors/useCompanyResource';
import { TabEmpty, TabSkeletonRows, DEFAULT_PER_PAGE } from '../../Vendors/companyTabUi';
import { useSectionPager } from '../../Vendors/sectionPager';
const ALL = 9999;

export default function CompanyCertificationsTab(props: {
  company: Company;
  companyCallback: any;
  userCanAdminCompany: boolean;
  uploadDocumentOpen?: boolean;
  onUploadDocumentClose?: () => void;
}) {
  const { company, companyCallback, userCanAdminCompany, uploadDocumentOpen: externalUploadDocumentOpen, onUploadDocumentClose } = props;
  const [internalUploadDocumentOpen, setInternalUploadDocumentOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);

  const uploadDocumentOpen = externalUploadDocumentOpen !== undefined ? externalUploadDocumentOpen : internalUploadDocumentOpen;

  const { items: certifications, count, loading, refetch } = useCompanyResource<CompanyCertification>(
    getCompanyCertifications,
    company.id,
    page,
    pageSize,
  );
  useSectionPager({ count, page, pageSize, setPage, setPageSize });
  // The upload form's site/application selectors need these collections, but the
  // rows read each cert's own sites/applications — so only fetch them (unpaged)
  // once the upload dialog is open, rather than on tab load.
  const { items: apps } = useCompanyResource<CompanyApplication>(getCompanyApps, company.id, 1, ALL, uploadDocumentOpen);
  const { items: sites } = useCompanyResource<Contact>(getCompanySites, company.id, 1, ALL, uploadDocumentOpen);

  // Company-shaped object the row + upload form read from.
  const cardCompany = useMemo(
    () => ({ ...company, companyCertifications: certifications, companyApplications: apps, locations: sites }),
    [company, certifications, apps, sites],
  );

  const handleUploadDocumentClose = () => {
    if (onUploadDocumentClose) {
      onUploadDocumentClose();
    } else {
      setInternalUploadDocumentOpen(false);
    }
  };

  // Keep the parent company in sync (for the not-yet-migrated header/labels) and refresh this page.
  const handleCertificationChange = useCallback(
    (updatedCompany: Company) => {
      companyCallback(updatedCompany);
      refetch();
    },
    [companyCallback, refetch],
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <StandardDialog title="Certification Upload" handleClose={handleUploadDocumentClose} isOpen={uploadDocumentOpen}>
        <CompanyCertificationForm company={cardCompany} handleClose={() => { handleUploadDocumentClose(); }} uploadCallback={handleCertificationChange} />
      </StandardDialog>

      <Box sx={tightTableSx}>
        <DataRow header>
          <DataCell xs={2}>Certification</DataCell>
          <DataCell xs={2}>Sites</DataCell>
          <DataCell xs={2}>Applications</DataCell>
          <DataCell xs={2}>Issue Date</DataCell>
          <DataCell xs={2}>End Date</DataCell>
          <DataCell xs={1}>Status</DataCell>
          <DataCell xs={1}>&nbsp;</DataCell>
        </DataRow>

        {loading && <TabSkeletonRows rows={4} columns={[2, 2, 2, 2, 2, 1, 1]} />}

        {!loading && certifications.length === 0 && <TabEmpty label='No certifications' />}

        {!loading && certifications.map((companyCert) => (
          <CompanyCertificationDataRow
            key={companyCert.id}
            companyCertification={companyCert}
            companySuccessCallback={handleCertificationChange}
            userCanAdminCompany={userCanAdminCompany}
            company={cardCompany}
          />
        ))}
      </Box>

    </Stack>
  );
}
